import {
  Autocomplete,
  Dialog,
  DialogTitle,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { useGetCustomerVehiclesQuery } from '../../redux/api'
import { CustomerEntry, VehicleEntry } from '../../sdk'
import {
  describeLicense,
  describeVehicle,
  describeVehicleYMM
} from '../../utils'
import { ClickableDeletableCard } from '../SharedUI'
import { NewVehicleTabs } from '../Vehicles/NewVehicleTabs'

interface Props {
  customer: CustomerEntry
  vehicle: VehicleEntry | undefined
  setVehicle: (vehicle: VehicleEntry | undefined) => void
  setCustomer: (customer: CustomerEntry | undefined) => void
  readOnly?: boolean
  autoFocus?: boolean
}

export const VehiclesByCustomerAutocomplete: React.FC<Props> = ({
  customer,
  vehicle,
  setVehicle,
  readOnly,
  autoFocus = false,
  setCustomer
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()

  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)

  const [inputValue, setInputValue] = useState('')

  const { data: vehicles, isLoading } = useGetCustomerVehiclesQuery({
    authorization: accessToken,
    shop: shopId,
    customer: customer.id!
  })
  useEffect(() => {
    if (vehicles && vehicles.length === 1) {
      setVehicle(vehicles[0])
    }
  }, [setVehicle, vehicles])

  const handleChange = (
    event: React.SyntheticEvent,
    value: VehicleEntry | null
  ) => {
    const data = (vehicles || []).filter(
      (vehicle) => vehicle.id === value?.id
    )[0]
    setVehicle(data)
  }

  const handleUnselectVehicle = useCallback(() => {
    setVehicle(undefined)
  }, [setVehicle])

  const submitNewVehicleCallback = useCallback(
    async (vehicle: VehicleEntry) => {
      setVehicle(vehicle)
      setCustomer(customer)
      setShowForm(false)
    },
    [customer, setCustomer, setVehicle]
  )

  const renderList = (
    _props: React.HTMLAttributes<HTMLLIElement>,
    option: VehicleEntry
  ) =>
    option.id !== '*' ? (
      <ListItem sx={{ padding: 0 }} {..._props} key={option.id}>
        <ListItemText
          primary={
            <Typography sx={{ fontWeight: 'bold' }}>
              {describeVehicleYMM(option.info!)}
            </Typography>
          }
          secondary={
            <>
              {option.info!.vin && (
                <Typography>Vin: {option.info!.vin}</Typography>
              )}
              {option.info!.licensePlate && (
                <Typography>
                  License Plate: {describeLicense(option.info!)}
                </Typography>
              )}
              {option.info!.name && (
                <Typography>Name: {option.info!.name}</Typography>
              )}
            </>
          }
        />
      </ListItem>
    ) : (
      <ListItem
        alignItems="center"
        key={option.id}
        disableGutters
        disablePadding>
        <ListItemButton
          onClick={() => {
            setVehicle(undefined)
            setShowForm(true)
          }}>
          <ListItemText
            primary="Create New Vehicle"
            primaryTypographyProps={{
              variant: 'button',
              textAlign: 'center',
              color: 'primary'
            }}
          />
        </ListItemButton>
      </ListItem>
    )

  return (
    <>
      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false)
        }}
        maxWidth="md"
        fullWidth>
        <DialogTitle>Add a New Vehicle</DialogTitle>
        <NewVehicleTabs
          customerId={customer.id!}
          submitCallback={submitNewVehicleCallback}
          setShowForm={setShowForm}
          licenseInput={inputValue}
        />
      </Dialog>

      {!vehicle && (
        <Autocomplete
          aria-label="vehicleInput"
          id="vehicle-selection"
          getOptionLabel={(vehicle) => {
            return (
              describeVehicle(vehicle.info) +
              describeLicense(vehicle.info!) +
              vehicle.info!.name
            )
          }}
          autoHighlight
          onChange={handleChange}
          options={[{ id: '*', info: {} }, ...(vehicles || [])]}
          autoComplete
          openOnFocus
          loading={isLoading}
          inputValue={inputValue}
          onInputChange={(_e: unknown, newValue) =>
            setInputValue(newValue || '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search vehicles by license plate"
              fullWidth
              autoFocus={autoFocus}
            />
          )}
          renderOption={renderList}
          noOptionsText={'No options found'}
          filterOptions={(options, state) => {
            // remove the '*' option from options
            const filtered = options.filter((option) => option.id !== '*')
            // do the regular filter operation
            const results = filtered.filter((option) => {
              const vehicleLabel =
                describeVehicle(option.info) +
                describeLicense(option.info!) +
                option.info!.name
              return vehicleLabel
                .toLowerCase()
                .includes(state.inputValue.toLowerCase())
            })
            // always include the '*' option
            return [{ id: '*', info: {} }, ...(results || [])]
          }}
        />
      )}
      {vehicle && (
        <ClickableDeletableCard
          label={
            <Stack>
              <Typography noWrap>{describeVehicle(vehicle.info!)}</Typography>
              <Typography variant="body2" noWrap>
                {vehicle.info!.licensePlate || 'N/A'}
              </Typography>
            </Stack>
          }
          onDelete={readOnly ? undefined : handleUnselectVehicle}
          onClick={() => navigate(AppRoutes.VEHICLES + '/' + vehicle.id)}
          aria-label="vehicleCard"
        />
      )}
    </>
  )
}
